import React, { useEffect, useState } from "react";

const STORAGE_KEY = "ul:xmas_banner:dismissed";

/** Return true if the banner should be shown now */
function shouldShowBanner() {
  // OPTIONAL: stop showing after a date (ET). Change/remove if you want.
  const end = new Date("2025-12-26T05:00:00-05:00");
  if (new Date() > end) return false;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const data = JSON.parse(raw);
    if (!data?.dismissed) return true;
    // Show again only after it expires
    if (typeof data.expiresAt === "number") {
      return Date.now() > data.expiresAt;
    }
    return false;
  } catch {
    return true;
  }
}

/**
 * Props:
 * - imageSrc: path to your banner image (default: "/assets/christmassale.png")
 * - href: optional URL to open when banner is clicked (default: "/")
 * - expireDays: how long to hide after close (default: 7)
 */
export default function HolidayBanner({
  imageSrc = "/assets/christmassale.png",
  href = "/",
  expireDays = 7,
  alt = "Christmas Sale — Use code XMAS10 at checkout",
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (shouldShowBanner()) setOpen(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const dismiss = () => {
    try {
      const expiresAt = Date.now() + expireDays * 24 * 60 * 60 * 1000;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ dismissed: true, expiresAt })
      );
    } catch {}
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Holiday sale banner"
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      {/* Backdrop (click to close) */}
      <div className="absolute inset-0 bg-black/60" onClick={dismiss} />

      {/* Banner card */}
      <div className="relative max-w-[min(92vw,900px)] shadow-2xl rounded-2xl overflow-hidden">
        {/* Close button */}
        <button
          aria-label="Close banner"
          onClick={dismiss}
          className="absolute right-2 top-2 rounded-full bg-black/70 hover:bg-black/90 text-white p-1.5 focus:outline-none focus:ring focus:ring-white/40 z-10"
        >
          {/* Simple X (no extra deps) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Click-through image */}
        {href ? (
          <a href={href} onClick={dismiss}>
            <img src={imageSrc} alt={alt} className="block w-full h-auto" />
          </a>
        ) : (
          <img src={imageSrc} alt={alt} className="block w-full h-auto" />
        )}
      </div>
    </div>
  );
}
